import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'

import { FormProvider, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { keyBy } from 'lodash'

import { useLazyFetchConfiguredServiceEventsQuery } from 'api/configured-service/configuredServiceCrud'
import { useLazyFetchWorkOrderListQuery } from 'api/work-order/fetchWorkOrderList'
import { useLazyFetchMetaFiltersQuery } from 'api/meta/getMetaFilters'
import { useEventsAccordion } from 'components/configured-service/accordions/EventsAccordion/hooks/useEventsAccordion'
import { useCSDetailsCachedData } from 'components/configured-service/useCSDetailsCachedData'
import {
  selectScheduledServiceEvents,
  selectIsDraftService,
  selectIsRecurringService,
  selectAccountId,
} from 'data/configured-service/configuredServiceDetailsSelectors'
import { selectServiceActions, selectServiceActionsById } from 'data/meta/metaSelectors'
import {
  setEvents,
  setSelectedDate,
  setCurrentMonth,
  setIsAddingEvent,
  markItemForDeletion,
  undoMarkItemForDeletion,
  setEditableItem,
  resetState,
  selectSelectedDate,
  selectCurrentMonth,
  selectIsAddingEvent,
} from 'data/configured-service/configuredServiceEventsSlice'

import { memo } from 'utils/react'
import { buildEventHeatmap } from 'utils/service'

import T from 'T'
import Constants from 'Constants'
import Drawer from '@mui/material/Drawer'
import HHDateCalendar from 'components/common/HHDateCalendar'
import RoutingCalendarLegend from 'components/customer-details/content/routing/RoutingCalendarLegend'
import { EventList } from 'components/configured-service/accordions/EventsAccordion/components'

import Box from '@mui/material/Box'
import { Divider } from '@mui/material'
import HHDialogTitle from 'components/common/HHDialogTitle'

const { MEDIA_SERVER } = Constants

const EventsDrawer = ({
  isOpenDrawer = false,
  configuredServiceId = '',
  expandWindowIfNeeded,
  ensureDateInWindow,
  isCalendarDataLoading = false,
  onClose,
  onRefetch,
}) => {
  const dispatch = useDispatch()

  const [fetchCSEvents, { data: eventsData }] = useLazyFetchConfiguredServiceEventsQuery()
  const [fetchMetaFilters, { data: metaFiltersData }] = useLazyFetchMetaFiltersQuery()
  const [fetchWorkOrderList, { data: workOrderListData }] = useLazyFetchWorkOrderListQuery()
  const { data: csData } = useCSDetailsCachedData({ id: configuredServiceId })
  const { saveEvents, getAvailableActionsForEvent, isMutationLoading } = useEventsAccordion({ id: configuredServiceId })

  const selectedDate = useSelector(selectSelectedDate)
  const currentMonth = useSelector(selectCurrentMonth)
  const isAddingEvent = useSelector(selectIsAddingEvent)

  const isDraft = useMemo(() => selectIsDraftService(csData), [csData])
  const isRecurringService = useMemo(() => selectIsRecurringService(csData), [csData])
  const accountId = useMemo(() => selectAccountId(csData), [csData])

  const disposalCountByWorkOrderId = useMemo(() => {
    const workOrders = workOrderListData?.workOrders
    if (!Array.isArray(workOrders) || workOrders.length === 0) return {}
    return workOrders.reduce((acc, wo) => {
      if (wo.workOrderId) {
        acc[wo.workOrderId] = wo.disposalTicketCount ?? 0
      }
      return acc
    }, {})
  }, [workOrderListData])

  const serviceActions = useMemo(() => selectServiceActions(metaFiltersData), [metaFiltersData])
  const serviceActionsById = useMemo(() => selectServiceActionsById(metaFiltersData), [metaFiltersData])
  const serviceActionByActionType = useMemo(() => keyBy(serviceActions, 'actionType'), [serviceActions])

  const serviceActionsRef = useRef(serviceActions)
  useEffect(() => {
    serviceActionsRef.current = serviceActions
  }, [serviceActions])

  const methods = useForm({
    defaultValues: {
      action: serviceActions?.length > 0 ? serviceActions[0] : null,
      events: {},
    },
  })
  const { control, setValue, getValues, reset } = methods

  const scheduledEvents = useMemo(
    () => selectScheduledServiceEvents({ events: eventsData, actions: serviceActions, isDraft }),
    [eventsData, serviceActions, isDraft]
  )

  const currentMonthEvents = useMemo(() => {
    if (!scheduledEvents || scheduledEvents.length === 0) return []
    return scheduledEvents.filter(({ serviceDateObj }) => {
      return serviceDateObj.getMonth() === currentMonth.getMonth() && serviceDateObj.getFullYear() === currentMonth.getFullYear()
    })
  }, [scheduledEvents, currentMonth])

  const eventsByDay = useMemo(() => {
    if (!currentMonthEvents || currentMonthEvents.length === 0) return {}
    return currentMonthEvents.reduce((acc, event) => {
      const { dayOfMonth } = event
      if (!acc[dayOfMonth]) {
        acc[dayOfMonth] = []
      }
      acc[dayOfMonth].push(event)
      return acc
    }, {})
  }, [currentMonthEvents])

  const selectedDayEvents = useMemo(() => {
    const selectedDayOfMonth = selectedDate.getDate()
    const events = eventsByDay[selectedDayOfMonth] || []
    return [...events].sort((a, b) => new Date(a.date) - new Date(b.date))
  }, [eventsByDay, selectedDate])

  const eventHeatmap = useMemo(
    () => buildEventHeatmap(scheduledEvents, { defaultIsRecurring: isRecurringService }),
    [scheduledEvents, isRecurringService]
  )
  const handleDateChange = useCallback(
    newDate => {
      dispatch(setSelectedDate(newDate))
      if (isAddingEvent) {
        dispatch(setIsAddingEvent(false))
        reset()
      }
    },
    [dispatch, isAddingEvent, reset]
  )

  const handleMonthChange = useCallback(
    newMonth => {
      dispatch(setCurrentMonth(newMonth))
    },
    [dispatch]
  )

  const handleAddEvent = useCallback(() => {
    setValue('action', serviceActions?.length > 0 ? serviceActions[0] : null)
    dispatch(setIsAddingEvent(true))
  }, [dispatch, setValue, serviceActions])

  const handleCancelAddEvent = useCallback(() => {
    dispatch(resetState())
    setValue('action', serviceActions?.length > 0 ? serviceActions[0] : null)
    if (configuredServiceId) {
      fetchCSEvents({ id: configuredServiceId })
    }
  }, [dispatch, setValue, serviceActions, configuredServiceId, fetchCSEvents])

  const handleDeleteEvent = useCallback(
    event => {
      dispatch(markItemForDeletion(event?.id))
    },
    [dispatch]
  )

  const handleUndoDeleteEvent = useCallback(
    event => {
      dispatch(undoMarkItemForDeletion(event?.id))
    },
    [dispatch]
  )

  const handleEditEvent = useCallback(
    event => {
      dispatch(setEditableItem(event?.id))
    },
    [dispatch]
  )

  const handleSaveEvent = useCallback(async () => {
    try {
      const formValues = getValues()
      const success = await saveEvents({
        formValues,
        id: configuredServiceId,
        scheduledEvents,
      })
      if (success) {
        setValue('action', serviceActions?.length > 0 ? serviceActions[0] : null)
        onRefetch()
      }
    } catch {
      // Error handling is done inside useEventsAccordion
    }
  }, [configuredServiceId, scheduledEvents, serviceActions, getValues, saveEvents, setValue, onRefetch])

  const handleDrawerClose = useCallback(() => {
    dispatch(resetState())
    onClose()
  }, [dispatch, onClose])

  useEffect(() => {
    if (isOpenDrawer && configuredServiceId) {
      const actions = serviceActionsRef.current
      dispatch(resetState())
      dispatch(setSelectedDate(new Date()))
      reset({ action: actions?.length > 0 ? actions[0] : null, events: {} })
      fetchCSEvents({ id: configuredServiceId })
      fetchMetaFilters({})
      if (accountId) {
        fetchWorkOrderList({ accountId, configuredServiceIds: [configuredServiceId] })
      }
    }
  }, [isOpenDrawer, configuredServiceId, accountId, dispatch, reset, fetchCSEvents, fetchMetaFilters, fetchWorkOrderList])

  useEffect(() => {
    if (scheduledEvents && serviceActionByActionType) {
      dispatch(setEvents(scheduledEvents))
      const events = {}
      scheduledEvents.forEach(e => {
        const { id: eventId, actionType, serviceDate } = e
        events[eventId] = {
          serviceDate,
          action: serviceActionByActionType[actionType],
        }
      })
      setValue('events', events)
    }
  }, [scheduledEvents, serviceActionByActionType])

  return (
    <Drawer
      anchor="right"
      open={isOpenDrawer}
      onClose={handleDrawerClose}
      sx={{ zIndex: theme => theme.zIndex.modal + 5 }}
      PaperProps={{ sx: { width: 400 } }}
    >
      <HHDialogTitle
        title={T.ADD_REMOVE_EVENT}
        icon={<img src={`${MEDIA_SERVER}CalendarCheck.svg`} alt="" width={16} />}
        onClose={handleDrawerClose}
        TitleTypographyProps={{
          variant: 'h6',
        }}
      />
      <Divider />
      <Box sx={{ px: 2, pt: 2 }}>
        <HHDateCalendar
          heatmap={eventHeatmap}
          isLoading={isCalendarDataLoading}
          showLoading={false}
          expandWindowIfNeeded={expandWindowIfNeeded}
          ensureDateInWindow={ensureDateInWindow}
          value={selectedDate || new Date()}
          onChange={handleDateChange}
          onMonthChange={handleMonthChange}
          legend={<RoutingCalendarLegend />}
          elevation={0}
        />
      </Box>
      <Box sx={{ px: 2, pb: '140px', mt: 1 }}>
        <FormProvider {...methods}>
          <EventList
            selectedDayEvents={selectedDayEvents}
            control={control}
            serviceActionsById={serviceActionsById}
            getAvailableActionsForEvent={(currentEvent, date) =>
              getAvailableActionsForEvent(currentEvent, date, serviceActions, selectedDayEvents)
            }
            onEdit={handleEditEvent}
            onDelete={handleDeleteEvent}
            onUndoDelete={handleUndoDeleteEvent}
            onAddEvent={handleAddEvent}
            onSave={handleSaveEvent}
            onCancel={handleCancelAddEvent}
            isMutationLoading={isMutationLoading}
            isDraft={isDraft}
            isRecurringService={isRecurringService}
            disposalCountByWorkOrderId={disposalCountByWorkOrderId}
            autocompleteSlotProps={{
              popper: { sx: { zIndex: theme => theme.zIndex.modal + 10 } },
            }}
            maxHeight="auto"
          />
        </FormProvider>
      </Box>
    </Drawer>
  )
}

EventsDrawer.propTypes = {
  isOpenDrawer: PropTypes.bool,
  configuredServiceId: PropTypes.string,
  expandWindowIfNeeded: PropTypes.func,
  ensureDateInWindow: PropTypes.func,
  isCalendarDataLoading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onRefetch: PropTypes.func.isRequired,
}

export default memo(EventsDrawer)
